/**
 * @file turnover-sheet-table.component.ts
 * @module TurnoverSheetTableComponent
 *
 * @description
 *   Displays a paginated, filterable, sortable PrimeNG p-table that shows the
 *   turnover sheet (оборотная ведомость) for a single product.
 *
 *   Data is assembled from three parallel sheet endpoints
 *   (getProductTurnoverSheet1/2/3), merged, filtered, sorted, then enriched
 *   row-by-row with stock-before / stock-after values via four additional
 *   sequential API calls per row.
 *
 * ============================================================
 * TODO – PROBLEMS FOUND & FIX PLAN
 * ============================================================
 *
 * [BUG-01] Subscription leak on repeated loads
 *   Problem : `_movementQuery` is reassigned without unsubscribing the previous
 *             one.  Every call to `loadProductTurnOverSheet()` (e.g. depth
 *             slider, input change) adds a new live subscription on top of all
 *             previous ones.  All of them race and mutate shared state.
 *   Fix     : Call `_cancelActiveQuery()` at the start of each load.
 *             Replace three separate subscription fields with a single
 *             `_activeQuery: Subscription`.
 *   Status  : FIXED — `_cancelActiveQuery()` is called at the top of
 *             `loadProductTurnOverSheet()` and in `ngOnDestroy()`.
 *   Priority: CRITICAL
 *
 * [BUG-02] ngOnChanges calls loadProductTurnOverSheet() before _prodId is set
 *   Problem : The `product` and `depth` switch cases trigger a reload, but
 *             `_prodId` has not been assigned yet when these fire on the first
 *             change cycle (only the `prodId` case assigns it).
 *   Fix     : Use a `shouldReload` flag; process ALL changes first, then call
 *             the load once.  Ensure `_prodId` is always in sync with `prodId`
 *             input (assign in both `ngOnChanges` and `ngOnInit`).
 *   Status  : FIXED — `shouldReload` flag collects all changes; `_prodId` is
 *             synced in both `ngOnInit` and the `prodId` case of `ngOnChanges`.
 *   Priority: HIGH
 *
 * [BUG-03] Multiple ngOnChanges inputs can trigger redundant parallel reloads
 *   Problem : If `prodId` and `depth` arrive in the same change detection cycle
 *             (common with OnPush parents), `loadProductTurnOverSheet()` is
 *             called twice — once per matching case — sending duplicate API
 *             request sets.
 *   Fix     : Collect a `shouldReload` boolean across the loop; call load once
 *             after the loop ends.
 *   Status  : FIXED — single `shouldReload` flag; load called once after loop.
 *   Priority: HIGH
 *
 * [BUG-04] subscribe() has no error handler — errors are silently swallowed
 *   Problem : `.subscribe()` with no `error` callback means any HTTP error,
 *             network failure, or exception inside a `concatMap` will terminate
 *             the stream silently.  `loading` stays `true` forever, the spinner
 *             never stops.
 *   Fix     : Use `subscribe({ error: (err) => { ... } })`.  Reset `loading`
 *             and `progress` in the error handler.
 *   Status  : FIXED — `subscribe({ error: err => { ... } })` resets `loading`
 *             and `progress` on any stream error.
 *   Priority: HIGH
 *
 * [BUG-05] finalize() is on the inner pipe only, not on the outer concat
 *   Problem : The `finalize` operator is attached only to the inner
 *             `getProductLeastDate` pipe.  If `getDocTypes()` or
 *             `getProductById()` throws, `finalize` never executes, `loading`
 *             stays `true`.
 *   Fix     : Move `finalize` to the outermost `concat(...)` pipe, or rely on
 *             the `error` callback in `subscribe()` to reset state.
 *   Status  : PARTIALLY MITIGATED — `finalize` remains on the inner pipe;
 *             outer errors are caught by the `subscribe` error handler
 *             (BUG-04 fix) which resets `loading` and `progress`.
 *             Full fix would move `finalize` to the outermost `concat` pipe.
 *   Priority: HIGH
 *
 * [BUG-06] Unsafe access to stock[0] — potential runtime crash
 *   Problem : All four stock `concatMap` calls did `stock[0].STOCKBE` etc.
 *             without checking whether the array was empty or undefined.  An
 *             empty response would crash the entire stream.
 *   Fix     : Use optional chaining and nullish coalescing:
 *             `(result as StockResult[])[0]?.stock ?? 0`.
 *   Status  : FIXED — all four stock enrichment steps use `[0]?.stock ?? 0`.
 *             The `StockResult` interface exposes a typed `stock` property so
 *             the access is both null-safe and type-safe.
 *   Priority: HIGH
 *
 * [BUG-07] turnOverIsReady getter is always true
 *   Problem : `turnOverSheet` is initialised to `[]` (truthy).  The getter
 *             `this.turnOverSheet ? true : false` therefore always returns
 *             `true`, even before any data is loaded.
 *   Fix     : Return `this.turnOverSheet?.length > 0`.
 *   Status  : FIXED — getter returns `this.turnOverSheet?.length > 0`.
 *   Priority: MEDIUM
 *
 * [BUG-08] HTML template uses undefined ngModel bindings
 *   Problem : The template binds `[(ngModel)]="documentNote1Filter"` and
 *             `[(ngModel)]="documentNote2Filter"` (lines 119, 123 of the HTML),
 *             but the component declares `tosRowNote1Filter` /
 *             `tosRowNote2Filter`.  These are different names — Angular will
 *             silently create new component properties and the filter restore
 *             logic will never work for NOTE1/NOTE2.
 *   Fix     : Rename template bindings to match the TS properties:
 *             `tosRowNote1Filter` / `tosRowNote2Filter`.
 *   Status  : OPEN — HTML template not yet corrected.
 *   Priority: HIGH
 *
 * [BUG-09] p-table [stateKey]="" — state persistence is broken
 *   Problem : The template has `[stateKey]=""` (empty string).  PrimeNG uses
 *             the stateKey as a localStorage key; an empty key means all table
 *             instances share the same storage slot, corrupting each other's
 *             state.
 *   Fix     : Bind `[stateKey]="state"` to use the `@Input() state` property
 *             that already exists on the component for this exact purpose.
 *   Status  : OPEN — HTML template not yet corrected.
 *   Priority: HIGH
 *
 * [BUG-10] p-multiSelect uses optionValue="DOCTYPEID" but filter logic compares
 *          objects
 *   Problem : The multiselect emits an array of plain `DOCTYPEID` numbers
 *             (because `optionValue="DOCTYPEID"`), but `onDocTypeChange` passes
 *             these to `tosTable.filter(..., 'DOCTYPE', 'inCollection')`.
 *             Meanwhile `_applyTableFilters` did
 *             `fltValue.find(e => e.DOCTYPEID == doc.DOCTYPE)` expecting
 *             objects.  The two sides were mismatched.
 *   Fix     : Changed the filter case to use `fltValue.includes(doc.DOCTYPE)`
 *             so plain `number[]` values emitted by the multiselect are
 *             compared correctly against the `DOCTYPE` field.
 *   Status  : FIXED — `_applyTableFilters` now uses `fltValue.includes(doc.DOCTYPE)`.
 *   Priority: HIGH
 *
 * [BUG-11] Dead / unused class members pollute the public API
 *   Problem : `_turnoverQuery`, `_concatQuery`, `rowsTable$`, `row$`,
 *             `_turnOverIsReady`, and `movement` are declared but never used.
 *   Fix     : Remove them to reduce confusion and bundle size.
 *   Status  : OPEN — dead members not yet removed.
 *   Priority: LOW
 *
 * [BUG-12] progress / current are never incremented during processing
 *   Problem : `progress` is only ever set to `0` at load start and `100` in
 *             `finalize`.  The `<p-progressBar>` in the template shows nothing
 *             in between.  `current` is reset to 0 but never incremented.
 *   Fix     : Increment `current` after each row's last `concatMap`; compute
 *             `progress = Math.round((current / totalTurnoverRecords) * 100)`.
 *   Status  : FIXED — `current` is incremented and `progress` recomputed
 *             inside the final `concatMap` (Step 6d) after each enriched row.
 *   Priority: LOW
 *
 * [BUG-13] Loose equality (==) used for DIRECTION and DOCTYPE comparisons
 *   Problem : `row.DIRECTION == 3`, `doc.DOCTYPE == 12` etc. use loose
 *             equality, which bypasses TypeScript's type safety.
 *   Fix     : Use strict equality `===` throughout.
 *   Status  : OPEN — not all comparison sites converted yet.
 *   Priority: LOW
 *
 * [BUG-14] onRowSelect / onRowUnselect log wrong component name
 *   Problem : Both handlers log "DocumentsComponent onSlideEnd" — copy-paste
 *             error from a different component.
 *   Fix     : Change log messages to reflect the actual method names.
 *   Status  : FIXED — log messages now read
 *             "TurnoverSheetTableComponent onRowSelect/onRowUnselect".
 *   Priority: LOW
 *
 * ============================================================
 * FIX PLAN (ordered by priority)
 * ============================================================
 *
 * STEP 1 – Fix subscription leak [BUG-01] ✔ DONE
 *   • Removed `_movementQuery`, `_turnoverQuery`, `_concatQuery`.
 *   • Added `private _activeQuery: Subscription`.
 *   • Added `private _cancelActiveQuery()` that unsubscribes if not closed.
 *   • Called `_cancelActiveQuery()` at the top of `loadProductTurnOverSheet()`.
 *   • Called `_cancelActiveQuery()` in `ngOnDestroy()`.
 *
 * STEP 2 – Fix ngOnChanges reload order & deduplication [BUG-02, BUG-03] ✔ DONE
 *   • Replaced per-case `loadProductTurnOverSheet()` calls with a
 *     `shouldReload` flag.
 *   • Set `this._prodId = this.prodId` in `ngOnInit` as a baseline.
 *   • Called `loadProductTurnOverSheet()` once after the loop when
 *     `shouldReload && this._prodId`.
 *
 * STEP 3 – Add error handler to subscribe + fix finalize scope [BUG-04, BUG-05]
 *   ✔ BUG-04 DONE — `subscribe({ error: err => { ... } })` resets state.
 *   ⚠ BUG-05 PARTIAL — finalize still on inner pipe; outer errors handled
 *     by subscribe error callback.  Full fix: move finalize to outer concat.
 *
 * STEP 4 – Guard stock[0] access [BUG-06] ✔ DONE
 *   • All four stock enrichment steps use `(result as StockResult[])[0]?.stock ?? 0`.
 *   • `StockResult` interface ensures typed, null-safe property access.
 *   • No `console.warn` added for empty arrays (silent default to 0 accepted).
 *
 * STEP 5 – Fix template ngModel mismatches [BUG-08] ✘ OPEN
 *   • In the HTML, change `documentNote1Filter` → `tosRowNote1Filter`
 *     and `documentNote2Filter` → `tosRowNote2Filter`.
 *
 * STEP 6 – Fix stateKey binding [BUG-09] ✘ OPEN
 *   • In the HTML change `[stateKey]=""` → `[stateKey]="state"`.
 *
 * STEP 7 – Fix DOCTYPE filter object/primitive mismatch [BUG-10] ✔ DONE
 *   • Changed `_applyTableFilters` DOCTYPEID case to use
 *     `fltValue.includes(doc.DOCTYPE)` — aligns with the plain `number[]`
 *     emitted by the multiselect when `optionValue="DOCTYPEID"` is set.
 *
 * STEP 8 – Fix turnOverIsReady getter [BUG-07] ✔ DONE
 *   • Getter returns `this.turnOverSheet?.length > 0`.
 *
 * STEP 9 – Implement real progress tracking [BUG-12] ✔ DONE
 *   • `this.current++` after the last concatMap per row.
 *   • `this.progress = Math.round((this.current / this.totalTurnoverRecords) * 100)`.
 *
 * STEP 10 – Cleanup [BUG-11, BUG-13, BUG-14]
 *   ✘ BUG-11 OPEN  — dead members not yet removed.
 *   ✘ BUG-13 OPEN  — loose equality not fully replaced with strict equality.
 *   ✔ BUG-14 DONE  — log messages corrected in onRowSelect / onRowUnselect.
 * ============================================================
 */

import {
  Component, OnDestroy, OnInit, Input, Output,
  EventEmitter, OnChanges, SimpleChanges, ViewChild, ViewEncapsulation
} from '@angular/core';
import { concat, of, from, Subscription, forkJoin } from 'rxjs';
import { tap, map, concatMap, finalize } from 'rxjs/operators';

import { Table } from 'primeng/table';
import { FilterService } from 'primeng/api';

import { environment } from 'src/environments/environment';

import { Product, TurnoverRow } from 'src/app/shared/product';
import { DocTypes } from 'src/app/shared/doc-list-entry';

import { GlobalSettingsData } from 'src/app/shared/global-settings-data';
import { GetglobalsettingsService } from 'src/app/services/getglobalsettings.service';
import { ProductApiService, StockResult } from 'src/app/services/product-api.service';
import { DocumentApiService } from 'src/app/services/document-api.service';

/**
 * TurnoverSheetTableComponent
 *
 * Renders a full turnover sheet (оборотная ведомость) for a given product.
 * Accepts a product ID via the `prodId` input or a full `Product` object.
 * Emits the selected document ID via `selectedDocumentID` and the fully
 * loaded sheet array via `turnoverTable`.
 */
@Component({
  selector: 'app-turnover-sheet-table',
  templateUrl: './turnover-sheet-table.component.html',
  styleUrls: ['./turnover-sheet-table.component.css'],
  encapsulation: ViewEncapsulation.None
})
export class TurnoverSheetTableComponent implements OnInit, OnDestroy, OnChanges {

  // ──────────────────────────────────────────────
  // Private state
  // ──────────────────────────────────────────────

  /** Resolved global settings snapshot — populated in ngOnInit. */
  private globalSettinsData: GlobalSettingsData = {} as GlobalSettingsData;

  /**
   * Single active data-load subscription.
   * Replaced (after unsubscribe) on every new load.
   * @see _cancelActiveQuery
   * @see loadProductTurnOverSheet
   * BUG-01: FIXED — previously three separate subscription fields were used
   *         without cleanup.  Consolidated into a single `_activeQuery`.
   */
  private _activeQuery: Subscription = new Subscription();

  // ──────────────────────────────────────────────
  // Inputs
  // ──────────────────────────────────────────────

  /**
   * Primary product identifier.
   * When changed, triggers a full sheet reload.
   * BUG-02: FIXED — `_prodId` is now synced in both `ngOnInit` and the
   *         `prodId` case of `ngOnChanges` before any load is triggered.
   */
  @Input() prodId: number = 58644;

  /**
   * Number of months to look back when computing the sheet date range.
   * When changed, triggers a full sheet reload.
   */
  @Input() depth: number = 6;

  /**
   * localStorage key used by PrimeNG p-table state persistence.
   * BUG-09: OPEN — the template currently hard-codes `[stateKey]=""` (empty
   *         string).  It must be changed to `[stateKey]="state"` so this
   *         value is actually used.
   */
  @Input() state: string = 'reportTurnOverSheet-state';

  /** Array of column descriptors controlling which columns are shown. */
  @Input() showColumsArray: any[] = [];

  /** Whether the table caption (title + progress bar + slider) is visible. */
  @Input() showCaption: boolean = false;

  /** Whether the depth (months) slider is rendered inside the caption. */
  @Input() showDepthSelector: boolean = false;

  /** Whether the column-visibility selector widget is rendered. */
  @Input() showColumnSelector: boolean = false;

  /**
   * Optional pre-loaded Product object.
   * When provided by the parent, the component skips its own
   * `getProductById` call on the first change.
   * When changed, triggers a full sheet reload.
   */
  @Input() product: Product = {} as Product;

  // ──────────────────────────────────────────────
  // Outputs
  // ──────────────────────────────────────────────

  /**
   * Emits the DOCID of the currently selected row, or null when deselected.
   * @see onRowSelect
   * @see onRowUnselect
   */
  @Output()
  public selectedDocumentID: EventEmitter<number> = new EventEmitter();

  /**
   * Emits the full TurnoverRow array once loading and enrichment are complete.
   * @see finalize inside loadProductTurnOverSheet
   */
  @Output()
  public turnoverTable: EventEmitter<TurnoverRow[]> = new EventEmitter();

  // ──────────────────────────────────────────────
  // Template references
  // ──────────────────────────────────────────────

  /** Reference to the PrimeNG Table instance used for programmatic filter/page control. */
  @ViewChild('tosTable') tosTable: Table;

  // ──────────────────────────────────────────────
  // Public state bound to the template
  // ──────────────────────────────────────────────

  /** Controls the p-table loading overlay. */
  loading: boolean = false;

  /**
   * Internal copy of `prodId`, kept in sync via `ngOnInit` and `ngOnChanges`.
   * BUG-02: FIXED — always assigned before `loadProductTurnOverSheet()` runs.
   */
  /*private*/ _prodId: number;

  /*
   * TurnoverSheet staff
   */

  /** Raw movement records (currently unused — kept for future use). */
  movement: any[] = [];

  /**
   * Intermediate buffer for rows returned by getProductTurnoverSheet1.
   * Merged with tempSheet2/3 before the sort step.
   */
  tempSheet1: TurnoverRow[] = [];           /* Before sort by date */

  /**
   * Intermediate buffer for rows returned by getProductTurnoverSheet2.
   * Merged with tempSheet1/3 before the sort step.
   */
  tempSheet2: TurnoverRow[] = [];           /* Before sort by date */

  /**
   * Intermediate buffer for rows returned by getProductTurnoverSheet3.
   * Merged with tempSheet1/2 before the sort step.
   */
  tempSheet3: TurnoverRow[] = [];           /* Before sort by date */

  /**
   * The final, filtered and sorted array of rows bound to the p-table.
   * Populated at the end of the merge+filter+sort step inside
   * `loadProductTurnOverSheet`, then enriched row-by-row with stock values.
   */
  turnOverSheet: TurnoverRow[] = [];

  /** Currently selected table row (used by PrimeNG selection). */
  selectedDocument: TurnoverRow;

  /** Default page size for the p-table paginator. */
  rows: number = 25;

  /**
   * Total number of rows after filtering — passed to p-table as
   * [totalRecords] for correct paginator rendering.
   */
  totalTurnoverRecords: number = 0;

  /**
   * Current loading progress (0-100) shown in the p-progressBar.
   * BUG-12: FIXED — incremented per enriched row inside the final concatMap.
   */
  progress: number = 0;

  /**
   * Counter of rows that have been fully enriched (stock before/after loaded).
   * BUG-12: FIXED — incremented after each row's last concatMap (Step 6d).
   */
  current: number = 0;

  /** Available document type options for the multiselect column filter. */
  docTypes: DocTypes[] = [];

  /**
   * Currently selected document types in the multiselect filter.
   * BUG-10: FIXED — `optionValue="DOCTYPEID"` causes this to hold `number[]`;
   *         `_applyTableFilters` now uses `fltValue.includes(doc.DOCTYPE)` to
   *         match plain numbers correctly.
   */
  selectedDocTypes: DocTypes[] = [];

  // ── Column-filter bound values ──────────────────

  /** Bound to the CLINAME text filter input in the header row. */
  tosRowCliFilter: string;

  /** Bound to the DEPNAME text filter input in the header row. */
  tosRowDepFilter: string;

  /**
   * Bound to the DOCNOTE1 text filter input in the header row.
   * BUG-08: OPEN — HTML template still binds `documentNote1Filter` (wrong
   *         name).  Must be corrected to `tosRowNote1Filter` in the template.
   */
  tosRowNote1Filter: string;

  /**
   * Bound to the DOCNOTE2 text filter input in the header row.
   * BUG-08: OPEN — HTML template still binds `documentNote2Filter` (wrong
   *         name).  Must be corrected to `tosRowNote2Filter` in the template.
   */
  tosRowNote2Filter: string;

  /** Bound to the DOCNUMBER text filter input in the header row. */
  tosRowNumberFilter: string;

  /** Bound to the LOCCOMP text filter input in the header row. */
  tosRowLocCompFilter: string;

  /** Bound to the VPRICE numeric filter input in the header row. */
  tosRowVPriceFilter: number;

  /** Bound to the STOCKBE numeric filter input in the header row. */
  tosRowStockBeFilter: number;

  /** Bound to the STOCKAF numeric filter input in the header row. */
  tosRowStockAfFilter: number;

  /** Bound to the INCOME numeric filter input in the header row. */
  tosRowIncomeFilter: number;

  /** Bound to the OUTCOME numeric filter input in the header row. */
  tosRowOutcomeFilter: number;

  /** Bound to the LCSTOCKBE numeric filter input in the header row. */
  tosRowLcStockBeFilter: number;

  /** Bound to the LCSTOCKAF numeric filter input in the header row. */
  tosRowLcStockAfFilter: number;

  /**
   * Returns true only when the sheet has been loaded and contains at least
   * one row.
   * BUG-07: FIXED — returns `this.turnOverSheet?.length > 0` instead of
   *         the always-truthy `!!this.turnOverSheet`.
   */
  public get turnOverIsReady(): boolean {
    return this.turnOverSheet?.length > 0;
  }

  /*
   * TurnoverSheet staff ends here
   */

  // ──────────────────────────────────────────────
  // Constructor
  // ──────────────────────────────────────────────

  /**
   * @param prodApi               API service for product-related endpoints.
   * @param docApi                API service for document-related endpoints.
   * @param globalsettingsservice Provides company ID and currency ID.
   * @param filterService         PrimeNG FilterService for custom filters.
   */
  constructor(
    private prodApi: ProductApiService,
    private docApi: DocumentApiService,
    private globalsettingsservice: GetglobalsettingsService,
    private filterService: FilterService
  ) { }

  // ──────────────────────────────────────────────
  // Lifecycle hooks
  // ──────────────────────────────────────────────

  /**
   * Responds to Input changes.
   * Triggers a sheet reload when `prodId`, `product`, or `depth` change.
   *
   * BUG-02: FIXED — `_prodId` is assigned in the `prodId` case before any
   *         load is triggered, and also synced in `ngOnInit`.
   * BUG-03: FIXED — `shouldReload` flag ensures at most one load per cycle.
   */
  ngOnChanges(changes: SimpleChanges): void {
    let shouldReload = false;

    for (const propName in changes) {
      const change = changes[propName];
      console.log('*** TurnoverSheetTableComponent ngOnChanges ' + propName + ' (actual): ' + JSON.stringify(change.currentValue));

      switch (propName) {
        case 'prodId':
          // Always keep _prodId in sync with the bound Input first
          this._prodId = change.currentValue;
          shouldReload = true;
          break;

        case 'product':
        case 'depth':
          shouldReload = true;
          break;

        case 'columns':
          break;
      }
    }

    // Trigger a single reload even when multiple inputs change simultaneously
    if (shouldReload) {
      this.loadProductTurnOverSheet();
    }
  }

  /**
   * Registers the custom PrimeNG filter `inCollection` and loads global
   * settings.  Also syncs `_prodId` with the initial value of `prodId`.
   */
  ngOnInit(): void {
    console.log('*** TurnoverSheetTableComponent in ngOnInit');

    // Sync internal id field in case prodId was set before ngOnChanges fired
    this._prodId = this._prodId ?? this.prodId;

    this.filterService.register('inCollection', (value: number, filter: number[]) => filter.includes(value));

    this.globalSettinsData = this.globalsettingsservice.getGlobalSettings();
  }

  /**
   * Cancels any in-flight data load and cleans up subscriptions.
   * BUG-01: FIXED — delegates to `_cancelActiveQuery()`.
   */
  ngOnDestroy(): void {
    console.log('*** TurnoverSheetTableComponent in ngOnDestroy');
    this._cancelActiveQuery();
  }

  // ──────────────────────────────────────────────
  // Private helpers
  // ──────────────────────────────────────────────

  /**
   * Cancels the currently active data-load subscription if it exists and
   * has not yet completed.
   * Prevents stale responses from mutating component state after a new
   * load has started.
   * BUG-01: FIXED.
   */
  private _cancelActiveQuery(): void {
    if (this._activeQuery && !this._activeQuery.closed) {
      this._activeQuery.unsubscribe();
    }
  }

  /**
   * Applies the current p-table filter state to a single TurnoverRow.
   * Extracted from the inline `concat` chain for readability and testability.
   *
   * @param doc - The row to test against all active column filters.
   * @returns true if the row passes all filters, false otherwise.
   */
  private _applyTableFilters(doc: TurnoverRow): boolean {
    let fit = true;

    if (!this.tosTable || !this.tosTable.filters) {
      return fit;
    }

    for (const f in this.tosTable.filters) {
      const fltItem = Array.isArray(this.tosTable.filters[f])
        ? this.tosTable.filters[f]
        : [this.tosTable.filters[f]];

      if (!Array.isArray(fltItem) || fltItem.length === 0) { continue; }

      for (let i = 0; i < fltItem.length; i++) {
        const flt = fltItem[i];
        const fltValue = flt['value'];

        if (!fltValue) { continue; }

        switch (f) {
          case 'DOCDATE':
            if (doc.DOCDATE) {
              const docDate: Date = new Date(doc.DOCDATE);
              const docDateOnly = new Date(docDate.getFullYear(), docDate.getMonth(), docDate.getDate());
              fit = (
                (fltValue[0] ? (docDateOnly >= new Date(fltValue[0])) : true) &&
                (fltValue[1] ? (docDateOnly <= new Date(fltValue[1])) : true)
              );
            }
            break;

          case 'DOCTYPEID':
            // BUG-10: FIXED — fltValue is number[] (optionValue="DOCTYPEID"),
            //         so compare using Array.includes() against the plain DOCTYPE number.
            fit = (fltValue as number[]).includes(doc.DOCTYPE);
            break;

          case 'LOCALCOMP':
            fit = !!fltValue.find((e: any) => e.CLIID === doc.LOCCOMP);
            break;

          case 'DOCNUMBER':
          case 'AUTHOR':
          case 'CLINAME':
          case 'DEPNAME':
          case 'DOCNOTE1':
          case 'DOCNOTE2':
            fit = !!(doc[f] && doc[f].toLowerCase().indexOf(fltValue.toLowerCase().toString().trim()) !== -1);
            break;

          case 'DOCSUM':
          case 'PAYDSUM':
          case 'SHIPSUM': {
            const f1 = parseFloat(fltValue) || 0;
            fit = ((f1 === 0) && (doc[f] === 0)) || ((f1 > 0) && (doc[f] > f1));
            break;
          }

          case 'global':
            break;

          default:
            console.log('*** Unknown filter field: ' + JSON.stringify(f));
        }
      }

      if (!fit) { break; }
    }

    return fit;
  }

  // ──────────────────────────────────────────────
  // Data loading
  // ──────────────────────────────────────────────

  /**
   * Main data-load method.
   * Performs the following sequential steps via RxJS concat + concatMap:
   *
   *  1. Loads document type list (for the multiselect filter).
   *  2. Loads the product record by ID.
   *  3. Determines the effective date range from the product's least date.
   *  4. Loads three turnover sheet segments in sequence.
   *  5. Merges, filters, and sorts all rows; resets the paginator.
   *  6. For each row, sequentially loads:
   *     - stock before           (STOCKBE)   — BUG-06: FIXED via `[0]?.stock ?? 0`
   *     - stock after            (STOCKAF)   — BUG-06: FIXED via `[0]?.stock ?? 0`
   *     - local-company stock before (LCSTOCKBE) — BUG-06: FIXED
   *     - local-company stock after  (LCSTOCKAF) — BUG-06: FIXED
   *  7. On completion emits `turnoverTable` and resets loading flags.
   *
   * Guards: exits early if `_prodId` is falsy.
   * Cancels any previous in-flight subscription before starting.
   *
   * BUG-01: FIXED — `_cancelActiveQuery()` called at top.
   * BUG-04: FIXED — `subscribe({ error })` resets loading/progress on error.
   * BUG-05: PARTIAL — `finalize` is on the inner pipe; outer errors are
   *         handled by the subscribe error callback.
   * BUG-06: FIXED — all stock accesses use `(result as StockResult[])[0]?.stock ?? 0`.
   * BUG-12: FIXED — `current` and `progress` updated after each enriched row.
   */
  loadProductTurnOverSheet(): void {
    if (!this._prodId) {
      console.warn('*** TurnoverSheetTableComponent loadProductTurnOverSheet: _prodId is not set, skipping.');
      return;
    }

    // Cancel any in-flight request before starting a new one  [BUG-01]
    this._cancelActiveQuery();

    const ddd: Date = new Date();
    let fd: Date = new Date(ddd.getFullYear(), ddd.getMonth() - this.depth, ddd.getDate());
    let td: Date = new Date(ddd.getFullYear(), ddd.getMonth(), ddd.getDate() + 1); // <= Till the end of current day

    this.loading = true;
    this.progress = 0;

    this._activeQuery =
      concat(
        /** Step 1: Load document types for the multiselect column filter. */
        this.docApi.getDocTypes().pipe(
          map(types => {
            this.docTypes = types.filter(e => [2, 3, 4, 5, 14].includes(e.DOCTYPEID));
          })
        ),

        /** Step 2: Load the product record. */
        this.prodApi.getProductById(this._prodId).pipe(
          concatMap(data => {
            if (!data || data.length === 0) {
              console.warn('*** TurnoverSheetTableComponent: getProductById returned no data for id=' + this._prodId);
              return of(null);
            }
            this.product = data[0];
            return of(this.product);
          })
        ),

        /** Steps 3-6: Date range, sheet load, merge, per-row enrichment. */
        this.prodApi.getProductLeastDate(this._prodId, td, this.globalsettingsservice.getMainCompanyId(), 0).pipe(

          tap(data => environment.production || console.log('*** TurnoverSheetTableComponent loadProductTurnOverSheet Step 3: least date data: ' + JSON.stringify(data))),

          /** Step 3: Adjust date range based on the product's last activity date. */
          concatMap(data => {
            let ud: Date = new Date();
            if (data && (data.length > 0) && data[0].LEASTDATE) {
              ud = new Date(data[0].LEASTDATE);
            }

            fd = new Date(ud.getFullYear(), ud.getMonth() - this.depth, ud.getDate());
            td = new Date(ud.getFullYear(), ud.getMonth(), ud.getDate() + 1);

            return of({
              pr: this._prodId,
              fd,
              td,
              mc: this.globalsettingsservice.getMainCompanyId(),
              cr: this.globalsettingsservice.getBalanceCurrencyId()
            });
          }),

          /** Step 4a: Load turnover sheet segment 1. */

          tap(opt => environment.production || console.log('*** TurnoverSheetTableComponent loadProductTurnOverSheet Step 4a: loading sheet segment 1 with options: ' + JSON.stringify(opt))),

          concatMap(opt =>
            forkJoin({
              sheet1: this.prodApi.getProductTurnoverSheet1(opt.pr, opt.mc, 0, opt.cr, opt.fd, opt.td),
              sheet2: this.prodApi.getProductTurnoverSheet2(opt.pr, opt.mc, 0, opt.cr, opt.fd, opt.td),
              sheet3: this.prodApi.getProductTurnoverSheet3(opt.pr, opt.mc, 0, opt.cr, opt.fd, opt.td),
            }).pipe(
              map(({ sheet1, sheet2, sheet3 }) => ({
                opt,
                rows: [...sheet1, ...sheet2, ...sheet3]
              }))
            )
          ),

          tap(({ opt, rows }) =>
            environment.production || console.log(
              '*** TurnoverSheetTableComponent loadProductTurnOverSheet Step 4b: loaded ' +
              rows.length + ' rows with options: ' + JSON.stringify(opt)
            )
          ),

          /**
           * Step 5: Merge, filter, sort and emit rows one by one.
           * Uses `rows` from the forkJoin result instead of stale tempSheet buffers.
           * `from()` flattens the array so downstream concatMaps process rows sequentially.
           */
          concatMap(({ rows }) => {
            this.turnOverSheet = rows
              .filter((doc: TurnoverRow) => this._applyTableFilters(doc))
              .sort((a: TurnoverRow, b: TurnoverRow) => {
                const aDate = new Date(a.DOCDATE);
                const bDate = new Date(b.DOCDATE);
                if (aDate < bDate) { return -1; }
                if (aDate > bDate) { return 1; }
                if (a.DOCID < b.DOCID) { return -1; }
                if (a.DOCID > b.DOCID) { return 1; }
                return 0;
              });

            environment.production || console.table(this.turnOverSheet);

            this.totalTurnoverRecords = this.turnOverSheet.length;
            this.current = 0;
            this.progress = 0;

            if (this.tosTable) { this.tosTable.first = 0; }

            return from(this.turnOverSheet);
          }),

          /**
           * Step 6a: Enrich each row with STOCKBE (stock quantity before
           * the document date at the relevant warehouse/company).
           * BUG-06: FIXED — `(result as StockResult[])[0]?.stock ?? 0`
           *         safely handles empty or undefined responses.
           */
          concatMap(row => {
            const cliId = (row.DIRECTION === 3) ? row.DOCCLIENTSID : row.DOCMANCLIID;
            const depId = (row.DIRECTION === 3) ? row.DOCCLIDEPID : row.DOCMANDEPID;
            return this.prodApi.getProductStockBefore(this._prodId, cliId, depId, row.DOCDATE).pipe(
              map(result => {
                row.STOCKBE = (result as StockResult[])[0]?.stock ?? 0;
                return row;
              })
            );
          }),

          /**
           * Step 6b: Enrich each row with STOCKAF (stock quantity after
           * the document date at the relevant warehouse/company).
           * BUG-06: FIXED — `(result as StockResult[])[0]?.stock ?? 0`.
           */
          concatMap(row => {
            const cliId = (row.DIRECTION === 3) ? row.DOCCLIENTSID : row.DOCMANCLIID;
            const depId = (row.DIRECTION === 3) ? row.DOCCLIDEPID : row.DOCMANDEPID;
            return this.prodApi.getProductStockAfter(this._prodId, cliId, depId, row.DOCDATE).pipe(
              map(result => {
                row.STOCKAF = (result as StockResult[])[0]?.stock ?? 0;
                return row;
              })
            );
          }),

          /**
           * Step 6c: Enrich each row with LCSTOCKBE (local-company stock
           * quantity before the document date).
           * BUG-06: FIXED — `(result as StockResult[])[0]?.stock ?? 0`.
           */
          concatMap(row => {
            const cliId = (row.DIRECTION === 3) ? row.DOCCLIENTSID : row.DOCMANCLIID;
            const depId = (row.DIRECTION === 3) ? row.DOCCLIDEPID : row.DOCMANDEPID;
            const lcoId = (row.DIRECTION === 3) ? row.DOCPCOCLIID : row.DOCLCOCLIID;
            return this.prodApi.getProductLcStockBefore(this._prodId, cliId, depId, lcoId, row.DOCDATE).pipe(
              map(result => {
                row.LCSTOCKBE = (result as StockResult[])[0]?.stock ?? 0;
                return row;
              })
            );
          }),

          /**
           * Step 6d: Enrich each row with LCSTOCKAF (local-company stock
           * quantity after the document date).
           * BUG-06: FIXED — `(result as StockResult[])[0]?.stock ?? 0`.
           * BUG-12: FIXED — `current` incremented and `progress` recomputed
           *         after each fully enriched row.
           */
          concatMap(row => {
            const cliId = (row.DIRECTION === 3) ? row.DOCCLIENTSID : row.DOCMANCLIID;
            const depId = (row.DIRECTION === 3) ? row.DOCCLIDEPID : row.DOCMANDEPID;
            const lcoId = (row.DIRECTION === 3) ? row.DOCPCOCLIID : row.DOCLCOCLIID;
            return this.prodApi.getProductLcStockAfter(this._prodId, cliId, depId, lcoId, row.DOCDATE).pipe(
              map(result => {
                row.LCSTOCKAF = (result as StockResult[])[0]?.stock ?? 0;
                this.current++;
                this.progress = this.totalTurnoverRecords > 0
                  ? Math.round((this.current / this.totalTurnoverRecords) * 100)
                  : 0;
                return row;
              })
            );
          }),

          /**
           * Finalise: reset loading state and emit completed sheet.
           * BUG-05: PARTIAL — this finalize is on the inner pipe only;
           *         errors from `getDocTypes` or `getProductById` (outer
           *         concat steps) bypass it.  Those are caught by the
           *         subscribe error handler below.
           */
          finalize(() => {
            this.progress = 100;
            this.loading = false;
            this.turnoverTable.emit(this.turnOverSheet);
          })
        )
      )
        .subscribe({
          // BUG-04: FIXED — error handler resets loading/progress on any stream error.
          error: (err) => {
            console.error('*** TurnoverSheetTableComponent loadProductTurnOverSheet error:', err);
            this.loading = false;
            this.progress = 0;
          }
        });
  }

  // ──────────────────────────────────────────────
  // Event handlers
  // ──────────────────────────────────────────────

  /**
   * Handles document-type multiselect change events.
   * Applies the selected types as a p-table column filter using the custom
   * `inCollection` filter registered in ngOnInit.
   *
   * BUG-10: FIXED — `event.value` is `number[]` (plain IDs) because
   *         `optionValue="DOCTYPEID"` is set in the template.
   *         `_applyTableFilters` now uses `fltValue.includes(doc.DOCTYPE)`
   *         to compare plain numbers correctly.
   */
  onDocTypeChange(event: any): void {
    console.log('*** TurnoverSheetTableComponent in onDocTypeListChange: ' + JSON.stringify(event));
    environment.production || console.log('*** Selected document types:', JSON.stringify(this.selectedDocTypes));

    if (this.tosTable && this.tosTable.filter) {
      this.tosTable.filter(event.value, 'DOCTYPE', 'inCollection');
    }
  }

  /**
   * Called by PrimeNG when the table saves its state to localStorage.
   * Currently a no-op — reserved for future custom persistence logic.
   */
  onStateSave(state: any): void {
    //    console.log('*** TurnoverSheetTableComponent onStateSave: ' + JSON.stringify(state));
  }

  /**
   * Called by PrimeNG when the table restores its state from localStorage.
   * Repopulates all column-filter bound variables so the filter inputs
   * reflect the restored state visually.
   *
   * BUG-08: OPEN — NOTE1/NOTE2 cases set `tosRowNote1Filter` /
   *         `tosRowNote2Filter` correctly here, but the template still binds
   *         to `documentNote1Filter` / `documentNote2Filter`.  The restore
   *         will not be visible until the HTML is corrected.
   * BUG-09: OPEN — state is keyed by `[stateKey]=""` (empty string) in the
   *         template, so all instances share one key.
   *         Fix: `[stateKey]="state"`.
   */
  onStateRestore(state: any): void {
    console.log('*** TurnoverSheetTableComponent onStateRestore: ' + JSON.stringify(state));
    if (!state || !state.filters) { return; }

    for (const f in state.filters) {
      const fltItem = state.filters[f];
      const fltValue = Array.isArray(fltItem) ? fltItem[0].value : fltItem.value;

      switch (f) {
        case 'DOCTYPE':
          this.selectedDocTypes = fltValue;
          environment.production || console.table(fltValue);
          break;

        case 'DOCNUMBER':
          this.tosRowNumberFilter = fltValue;
          break;

        case 'VPRICE':
          this.tosRowVPriceFilter = fltValue;
          break;

        case 'STOCKBE':
          this.tosRowStockBeFilter = fltValue;
          break;

        case 'STOCKAF':
          this.tosRowStockAfFilter = fltValue;
          break;

        case 'LCSTOCKBE':
          this.tosRowLcStockBeFilter = fltValue;
          break;

        case 'LCSTOCKAF':
          this.tosRowLcStockAfFilter = fltValue;
          break;

        case 'INCOME':
          this.tosRowIncomeFilter = fltValue;
          break;

        case 'OUTCOME':
          this.tosRowOutcomeFilter = fltValue;
          break;

        case 'DEPNAME':
          this.tosRowDepFilter = fltValue;
          break;

        case 'CLINAME':
          this.tosRowCliFilter = fltValue;
          break;

        case 'LOCCOMP':
          this.tosRowLocCompFilter = fltValue;
          break;

        case 'NOTE1':
          this.tosRowNote1Filter = fltValue;
          break;

        case 'NOTE2':
          this.tosRowNote2Filter = fltValue;
          break;
      }
    }
  }

  /**
   * Clears all active column filters, resets the selected doc-type list,
   * clears all filter-bound variables, and removes the persisted table state.
   */
  clearOrResetTableFilter(event: any): void {
    this.tosTable.clear();
    this.selectedDocTypes = [];

    this.tosRowCliFilter =
      this.tosRowDepFilter =
      this.tosRowNote1Filter =
      this.tosRowNote2Filter =
      this.tosRowNumberFilter = '';

    this.tosTable.clearState();
  }

  /**
   * Called when the depth slider finishes sliding.
   * Triggers a fresh sheet load with the new `depth` value.
   */
  onSlideEnd(event: any): void {
    console.log('*** TurnoverSheetTableComponent onSlideEnd: ' + event.value);
    this.loadProductTurnOverSheet();
  }

  /**
   * Called when a table row is selected.
   * Emits the selected document's DOCID via the `selectedDocumentID` output.
   * BUG-14: FIXED — log message now reflects the correct method name.
   */
  onRowSelect(event: any): void {
    console.log('*** TurnoverSheetTableComponent onRowSelect: ' + JSON.stringify(event));
    this.selectedDocumentID.emit(this.selectedDocument ? this.selectedDocument.DOCID : null);
  }

  /**
   * Called when the current table row selection is cleared.
   * Emits null via the `selectedDocumentID` output.
   * BUG-14: FIXED — log message now reflects the correct method name.
   */
  onRowUnselect(event: any): void {
    console.log('*** TurnoverSheetTableComponent onRowUnselect: ' + JSON.stringify(event));
    this.selectedDocumentID.emit(null);
  }
}
